import React, { useState, useContext } from 'react';
import {
  Typography,
  Container,
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import {
  LocalParking as ParkingIcon,
  Home as HomeIcon,
  Map as MapIcon,
  Settings as SettingsIcon,
  Crop as RoiIcon,
  CheckCircle as ValidationIcon,
  LiveTv as LiveIcon,
  Folder as FolderIcon,
  BugReport as TestingIcon,
  Storage as StorageIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeContext } from '../App';
import { responsiveSpacing } from '../styles/responsive';
import DeviceTestingPanel from '../components/DeviceTestingPanel';
import { getUserInfo } from '../utils/auth';
import { hasRoleAtLeast, normalizeRole, UserRole } from '../utils/permissions';
import UserProfileMenu from '../components/UserProfileMenu';
import ProjectPageHeader from '../components/ProjectPageHeader';

interface LayoutViewProps {
  children: React.ReactNode;
}

const LayoutView: React.FC<LayoutViewProps> = ({ children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const { mode, toggleTheme } = useContext(ThemeContext);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [testingPanelOpen, setTestingPanelOpen] = useState(false);

  const userInfo = getUserInfo();

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'admin':
        return '관리자 모드';
      case 'editor':
        return '편집자 모드';
      case 'viewer':
        return '뷰어 모드';
      default:
        return '뷰어 모드';
    }
  };

  // 메뉴 정의 — `requireRole` 미달인 항목은 사이드바에서 숨긴다.
  // backend RequireRole 매트릭스와 sync. UI 가드 + defense-in-depth.
  type MenuItem = {
    id: string;
    title: string;
    path: string;
    icon: React.ReactNode;
    requireRole?: UserRole;
  };

  const currentRole = normalizeRole(userInfo?.role);

  const allMenuItems: MenuItem[] = [
    { id: 'home', title: '프로젝트 선택', path: '/', icon: <HomeIcon /> },
    { id: 'map-editor', title: '맵 에디터', path: `/project/${projectId}/map-editor`, icon: <MapIcon />, requireRole: 'editor' },
    { id: 'map-properties', title: '맵 속성 편집기', path: `/project/${projectId}/map-properties`, icon: <SettingsIcon />, requireRole: 'editor' },
    { id: 'roi-editor', title: 'ROI 편집기', path: `/project/${projectId}/roi-editor`, icon: <RoiIcon />, requireRole: 'editor' },
    { id: 'learning-data-management', title: '학습 데이터 관리', path: `/project/${projectId}/learning-data-management`, icon: <StorageIcon />, requireRole: 'editor' },
    { id: 'parking-validation', title: '주차면 검증', path: `/project/${projectId}/parking-validation`, icon: <ValidationIcon />, requireRole: 'editor' },
    { id: 'live-status', title: '실시간 현황', path: `/project/${projectId}/live-status`, icon: <LiveIcon /> },
    { id: 'file-repository', title: '파일 보관함', path: `/project/${projectId}/file-repository`, icon: <FolderIcon />, requireRole: 'admin' },
  ];

  const menuItems: MenuItem[] = allMenuItems.filter(
    (item) => !item.requireRole || hasRoleAtLeast(currentRole, item.requireRole),
  );

  const handleMenuClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const projectTitle = projectId ? `프로젝트 ${projectId}` : '주차 관리 시스템';

  const getPageTitle = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment === 'learning-data-management') return '학습 데이터 관리';
    if (lastSegment === 'parking-validation') return '주차면 검증';
    if (lastSegment === 'live-status') return '실시간 주차 관제';
    if (lastSegment === 'file-repository') return '파일 보관함';
    if (lastSegment === 'roi-editor') return 'ROI 편집기';
    if (lastSegment === 'deployments') return '배포 결과';

    return '주차 관리 시스템';
  };

  /** 현재 페이지 라벨에 매칭되는 breadcrumb 아이콘. */
  const getPageIcon = (): React.ReactNode => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment === 'learning-data-management') return <StorageIcon fontSize="inherit" />;
    if (lastSegment === 'parking-validation') return <ValidationIcon fontSize="inherit" />;
    if (lastSegment === 'live-status') return <LiveIcon fontSize="inherit" />;
    if (lastSegment === 'file-repository') return <FolderIcon fontSize="inherit" />;
    if (lastSegment === 'roi-editor') return <RoiIcon fontSize="inherit" />;
    if (lastSegment === 'deployments') return <MapIcon fontSize="inherit" />;
    return null;
  };

  return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Header — 공통 ProjectPageHeader (현장 목록 > [현장명] > [페이지명] breadcrumb) */}
        <ProjectPageHeader
          currentPageLabel={getPageTitle()}
          currentPageIcon={getPageIcon()}
          rightExtras={
            userInfo ? (
              <Chip
                label={getRoleLabel(userInfo.role)}
                size="small"
                color={userInfo.role === 'admin' ? 'error' : userInfo.role === 'editor' ? 'primary' : 'default'}
                sx={{ fontWeight: 500 }}
              />
            ) : undefined
          }
        />

        {/* Mobile Navigation Drawer */}
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: 280,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ParkingIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" color="primary.main">
                {projectTitle}
              </Typography>
            </Box>
            <Divider />
          </Box>

          <List>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.id}
                selected={location.pathname === item.path}
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? 'inherit' : 'action.active' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname === item.path ? 600 : 400
                  }}
                />
              </ListItemButton>
            ))}

            {/* Testing Panel Menu Item */}
            <Divider sx={{ my: 1 }} />
            <ListItemButton
              onClick={() => {
                setTestingPanelOpen(true);
                setMobileMenuOpen(false);
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                }
              }}
            >
              <ListItemIcon>
                <TestingIcon />
              </ListItemIcon>
              <ListItemText
                primary="크로스 디바이스 테스팅"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 400
                }}
              />
            </ListItemButton>

            {/* Theme Toggle Menu Item */}
            <ListItemButton
              onClick={() => {
                toggleTheme();
                setMobileMenuOpen(false);
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                }
              }}
            >
              <ListItemIcon>
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </ListItemIcon>
              <ListItemText
                primary={mode === 'dark' ? '라이트 모드' : '다크 모드'}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 400
                }}
              />
            </ListItemButton>
          </List>
        </Drawer>

        <Container
          component="main"
          maxWidth={false}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            ...responsiveSpacing.pagePadding
          }}
        >
          {children}
        </Container>

        {/* Device Testing Panel */}
        <DeviceTestingPanel
          open={testingPanelOpen}
          onClose={() => setTestingPanelOpen(false)}
        />
      </Box>
  );
};

export default LayoutView; 